import { useMutation } from '@apollo/client/react';
import { CREATE_RECEIPT_PDF_MUTATION } from '../../integrations/allkort/graphql/gql/ReceiptPdfMutation';
import {
	CreateReceiptPdfResponse
} from '../../integrations/allkort/graphql/interface/ReceiptPdf';

/**
 * useCreateReceiptPdf
 * 
 * Custom hook for generating a PDF receipt for a transaction.
 * Returns a URL that can be used to download the receipt.
 * 
 * @example
 * ```tsx
 * const { createReceiptPdf, loading, error, data } = useCreateReceiptPdf();
 * 
 * // Generate receipt PDF
 * const handleDownload = async (transactionId: string) => {
 *   try {
 *     const result = await createReceiptPdf(transactionId);
 *     if (result?.url) {
 *       window.open(result.url, '_blank');
 *     }
 *   } catch (err) {
 *     console.error('Failed to generate receipt:', err);
 *   }
 * };
 * ```
 */
export const useCreateReceiptPdf = () => {
	const [createReceiptPdfMutation, { loading, error, data }] = useMutation<
		CreateReceiptPdfResponse,
		{ id: string }
	>(CREATE_RECEIPT_PDF_MUTATION);

	/**
	 * Generate a PDF receipt for a transaction
	 * @param transactionId - The transaction ID (base64 encoded)
	 * @returns The PDF data with URL and expiration date, or null if failed
	 */
	const createReceiptPdf = async (transactionId: string) => {
		try {
			const result = await createReceiptPdfMutation({
				variables: { id: transactionId }
			});

			// Log error details if mutation failed
			if (result.error) {
				console.error('Failed to create receipt PDF:', result.error.message);
				if ('graphQLErrors' in result.error && Array.isArray(result.error.graphQLErrors) && result.error.graphQLErrors.length > 0) {
					console.error('GraphQL errors:', result.error.graphQLErrors);
				}
				if ('networkError' in result.error && result.error.networkError) {
					console.error('Network error:', result.error.networkError);
				}
			}

			if (result.data?.createReceiptPdf?.pdfData) {
				return result.data.createReceiptPdf.pdfData;
			}
			return null;
		} catch (err) {
			console.error('Error creating receipt PDF:', err);
			throw err;
		}
	};

	return {
		createReceiptPdf,
		loading,
		error,
		data: data?.createReceiptPdf?.pdfData ?? null
	};
};
